import { ShadowRootTheGoodPart } from "lwc";

export type BooleanAttr = boolean | string | null;

/**
 * Converts an attribute to a boolean
 */
export function attrToBool<T>(attr: T) {
  if (typeof attr === "boolean") {
    return attr;
  }
  if (typeof attr === "string") {
    return attr === "true" || attr === "";
  }
  return !!attr;
}

/**
 * Converts an attribute to a number
 */
export function attrToNumber<T>(attr: T) {
  return Number(attr);
}

/**
 * Creates a short content-based hash
 */
export function hash(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    let char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Flattens a list of options. Used option groups need to be
 * flattened into an array for easy parsing
 */
export function flattenOptions<T>(options: T[]) {
  return options.flatMap((o) =>
    // @ts-ignore
    Array.isArray(o.options) ? (o.options as T[]) : o
  );
}

/**
 * Builds an asset URL using environment context.
 */
export function getAssetsUrl(fileName: string) {
  if (/^data:image/.test(fileName)) return fileName;
  // process.env.IMPORT_META_URL is replaced at build time
  // based on the build target (import.meta.url for web
  // builds and the CDN url for npm builds)
  // https://github.com/facebook/jest/issues/11634
  const base = (process.env.IMPORT_META_URL ?? "/").replace(/\/modules.*/, "");
  return [base, fileName].map((p) => p.replace(/^\/|\/$/, "")).join("/");
}

/**
 * Fetches a remote asset.
 */
export async function fetchAsset(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) reject(text);

        resolve(text);
      })
      .catch((e) => reject(e));
  });
}

/**
 * returns the text content of provided html string
 * Note: this method cannot be tested as innerText is not available in JSDOM
 */
export function htmlToText(html: string) {
  const span = document.createElement("span");
  span.innerHTML = html;
  // only send visible content, exclude style, script and hidden content from text
  return span.innerText; // DO NOT change to span.textContent
}

/**
 * Expands LWC's proxy objects to JSON for easier viewing
 */
export function getProxyValue<T>(proxy: unknown) {
  return JSON.parse(JSON.stringify(proxy)) as T;
}

/**
 * Waits until an element exists
 */
export async function exists(
  selector: string,
  node: HTMLElement | ShadowRootTheGoodPart,
  options?: { timeout?: number }
) {
  const timeout = options?.timeout ?? Infinity;
  const interval = 30;
  let timer = 0;
  let intervalId: NodeJS.Timer;
  return new Promise<Element | null>((resolve) => {
    intervalId = setInterval(() => {
      try {
        const el = node.querySelector(selector);
        if (el) {
          clearInterval(intervalId);
          return resolve(el);
        } else {
          timer += interval;
          if (timer >= timeout) {
            return resolve(null);
          }
        }
      } catch (error) {
        clearInterval(intervalId);
      }
    }, interval);
  });
}

export type Primitive =
  | boolean
  | number
  | string
  | symbol
  | null
  | undefined
  | BigInt;

/**
 * Remove duplicates from an array of primitives.
 * @param array - array to dedupe.
 */
export function dedupe<T extends Primitive>(array?: T[]) {
  return [...new Set(array)];
}
